import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiX, FiCheck } from 'react-icons/fi';
import { uploadFile } from './helpers/uploadFile';
import { crudStorage } from './helpers/crudStorage';

export default function UploadModal({ isOpen, onClose, dashState, onSuccess, initialFile }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const fileInputRef = React.useRef(null);

  // Set initial file if provided (from drag and drop)
  useEffect(() => {
    if (initialFile) {
      setSelectedFile(initialFile);
    }
  }, [initialFile]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
    }
  }, [isOpen]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    
    try {
      // Always upload to Client Uploads folder
      const path = dashState?.patient?.pid ? `${dashState.patient.pid}/` : "";
      const uploadPath = `${path}Client Uploads/`;
      
      // Ensure Client Uploads folder exists
      await crudStorage(
        dashState,
        "createDirectory",
        "",
        dashState.organization?.s3BucketName || "brightlighthealth",
        uploadPath
      ).catch(() => {}); // Ignore error if folder already exists
      
      // Upload the file
      await uploadFile(
        dashState,
        uploadPath,
        selectedFile,
        toast,
        () => {}
      );
      
      toast({
        title: "File uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Refresh files list
      if (onSuccess) {
        await onSuccess();
      }
      
      // Close modal after success
      onClose();
      
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Could not upload the file",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.300" />
      <ModalContent>
        <ModalHeader>Upload File</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4}>
            <Box
              w="full"
              h="180px"
              border="2px dashed"
              borderColor={selectedFile ? "green.400" : "gray.300"}
              borderRadius="lg"
              bg="white"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !selectedFile && fileInputRef.current?.click()}
            >
              <Icon 
                as={selectedFile ? FiCheck : FiUpload} 
                fontSize="40px" 
                color={selectedFile ? "green.500" : "gray.400"} 
                mb={2} 
              />
              {!selectedFile ? (
                <>
                  <Text fontSize="md" color="gray.600">
                    Drop file here or click to browse
                  </Text>
                  <Text fontSize="sm" color="gray.400" mt={1}>
                    Max 100MB
                  </Text>
                </>
              ) : (
                <VStack spacing={1}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700" textAlign="center" px={4}>
                    {selectedFile.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {formatFileSize(selectedFile.size)}
                  </Text>
                  {!isUploading && (
                    <Text 
                      fontSize="xs" 
                      color="blue.500" 
                      cursor="pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                      }}
                      _hover={{ textDecoration: "underline" }}
                    >
                      Change file
                    </Text>
                  )}
                </VStack>
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                onChange={handleFileSelect}
              />
            </Box>
            
            <Text fontSize="xs" color="gray.500" textAlign="center">
              File will be uploaded to "Client Uploads" folder
            </Text>
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUploading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleUpload}
            isLoading={isUploading}
            loadingText="Uploading..."
            isDisabled={!selectedFile || isUploading}
            leftIcon={<Icon as={FiUpload} />}
          >
            Upload
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}